/**
 * Utkarsh design tokens — classic amber (live) + logo-brand preview palette.
 */
export const utkarshColors = {
  amber: '#F9B101',
  ink: '#141414',
  bgWarm: '#FAF9F6',
  inputBg: '#F3F1EC',
  border: '#E2DED2',
  secondaryText: '#5C574C',
  placeholderGray: '#9C9992',
  amberTint: '#FEF2D3',
  white: '#FFFFFF',
  onAccent: '#141414',
  secondary: '#F9B101',
  onSecondary: '#141414',
  secondaryTint: '#FEF2D3',
  success: '#2E7D32',
  error: '#C62828',
  errorSoft: '#E57373',
  warning: '#B86E00',
  info: '#1B4D3E',
  dark: {
    bg: '#141414',
    surface: '#1C1B19',
    surfaceElevated: '#242220',
    inputBg: '#2A2825',
    border: '#3D3A35',
    text: '#FAF9F6',
    secondaryText: '#A8A49A',
    placeholder: '#7A766E',
    amberTint: '#3D3208',
    errorTint: '#3A1F1F',
  },
  errorTint: '#FDECEC',
};

/**
 * Extracted from logo close-ups (cool figure + warm figure).
 * Primary = left blues · Secondary = right orange / magenta — no classic yellow.
 *
 * Primary `#1E7AD8` · Secondary `#E87820` · Magenta `#D92B5A` · Cyan `#40B0F0`
 */
export const logoBrandColors = {
  /** Semantic primary (utkarsh.amber) — vibrant azure from blue figure */
  amber: '#1E7AD8',
  ink: '#0A1628',
  bgWarm: '#F4F8FC',
  inputBg: '#E8F1F9',
  border: '#C9DAEA',
  secondaryText: '#45617A',
  placeholderGray: '#8499AE',
  amberTint: '#E5F1FB',
  white: '#FFFFFF',
  onAccent: '#FFFFFF',
  /** Warm figure — tangerine CTA / charts / secondary actions */
  secondary: '#E87820',
  onSecondary: '#FFFFFF',
  secondaryTint: '#FFF1E5',
  success: '#2FA84A',
  error: '#C62828',
  errorSoft: '#E57373',
  warning: '#E87820',
  info: '#40B0F0',
  royal: '#1E7AD8',
  cyan: '#40B0F0',
  orange: '#E87820',
  magenta: '#D92B5A',
  navy: '#102F9C',
  dark: {
    bg: '#07101C',
    surface: '#0F1C2E',
    surfaceElevated: '#162840',
    inputBg: '#1A304A',
    border: '#2A4563',
    text: '#F4F8FC',
    secondaryText: '#9BB0C7',
    placeholder: '#6E849C',
    amberTint: '#0C2A4A',
    errorTint: '#3A1F1F',
  },
  errorTint: '#FDECEC',
  brandScale: {
    50: '#E5F1FB',
    100: '#B8D9F7',
    200: '#8BC0F2',
    300: '#5EA6EC',
    400: '#3B91E5',
    500: '#1E7AD8',
    600: '#1968B8',
    700: '#145698',
    800: '#0F4478',
    900: '#0A3258',
  },
  secondaryScale: {
    50: '#FFF1E5',
    100: '#FFD9B8',
    200: '#FFC08A',
    300: '#FFA65C',
    400: '#F58C38',
    500: '#E87820',
    600: '#C96418',
    700: '#A95012',
    800: '#893C0D',
    900: '#6A2C09',
  },
};

/**
 * Merchant Ops — rebuilt from proven product systems (not logo inventing).
 *
 * References adapted for Utkarsh (buyer–seller matching + staff KYC admin):
 * - Shopify Polaris: primary action green `#008060`, charcoal ink `#202223`,
 *   cool grey canvas `#F6F6F7`, interactive blue `#2C6ECB`
 * - Stripe Dashboard: cool near-white surfaces, deep navy text hierarchy,
 *   one accent for CTAs, hairline borders
 * - Airbnb marketplace: Babu teal `#00A699` as success / partner accent
 *   (warm coral left out — failed earlier; marketplace energy via blue secondary)
 *
 * Why this fits: Utkarsh admin is closer to Shopify (manage sellers, listings,
 * verification) than to a consumer marketing site. Restraint > loud gradients.
 *
 * Primary `#008060` · Secondary `#2C6ECB` · Success `#00A699` · Ink `#202223`
 */
export const matchClarityColors = {
  amber: '#008060',
  ink: '#202223',
  bgWarm: '#F6F6F7',
  inputBg: '#FFFFFF',
  border: '#E1E3E5',
  secondaryText: '#6D7175',
  placeholderGray: '#8C9196',
  amberTint: '#E3F1EC',
  white: '#FFFFFF',
  onAccent: '#FFFFFF',
  secondary: '#2C6ECB',
  onSecondary: '#FFFFFF',
  secondaryTint: '#EAF1FB',
  success: '#00A699',
  error: '#D72C0D',
  errorSoft: '#E57373',
  warning: '#C05717',
  info: '#2C6ECB',
  royal: '#008060',
  cyan: '#00A699',
  orange: '#C05717',
  magenta: '#9C6ADE',
  navy: '#0B3D32',
  fonts: {
    heading: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    body: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  },
  dark: {
    bg: '#0B0C0D',
    surface: '#1A1C1D',
    surfaceElevated: '#242628',
    inputBg: '#2C2E2F',
    border: '#3D4043',
    text: '#E3E5E7',
    secondaryText: '#A0A4A8',
    placeholder: '#6D7175',
    amberTint: '#0F2E24',
    errorTint: '#3A1A12',
  },
  errorTint: '#FBEAE5',
  brandScale: {
    50: '#E3F1EC',
    100: '#B7DED0',
    200: '#86C7B0',
    300: '#54AF90',
    400: '#2C9A78',
    500: '#008060',
    600: '#006E52',
    700: '#005C45',
    800: '#004A37',
    900: '#003829',
  },
  secondaryScale: {
    50: '#EAF1FB',
    100: '#C5D7F4',
    200: '#9BBBEB',
    300: '#719FE2',
    400: '#4E87D7',
    500: '#2C6ECB',
    600: '#255DB0',
    700: '#1E4D94',
    800: '#183D77',
    900: '#112E5B',
  },
};

export const PALETTE_IDS = ['classic', 'logoBrand', 'matchClarity'];

export const PALETTE_STORAGE_KEY = 'utkarsh-admin-palette';

export const utkarshRadii = {
  sm: '12px',
  md: '14px',
  lg: '16px',
  pill: '999px',
};

export const utkarshSpace = {
  screenPadding: '16px',
  fieldGap: '20px',
  buttonPy: '16px',
};
